/**
 * SearchApiKeyManager 组件
 *
 * 搜索供应商 Multi-Key 管理。
 * 参考 LLM ApiKeyManager 设计，支持同一供应商下的多个 API 密钥。
 */

import React, { useState, useCallback, useEffect } from 'react';
import { clsx } from 'clsx';
import { StatusBadge } from '../shared';
import {
  searchAdminApi,
  type SearchApiKeyInfo,
} from '../../../services/searchAdminApi';

export interface SearchApiKeyManagerProps {
  providerName: string;
  disabled?: boolean;
  className?: string;
  onRefresh?: () => void;
  onKeysChange?: (keys: SearchApiKeyInfo[]) => void;
}

const statusVariantMap: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  enabled: 'success',
  active: 'success',
  disabled: 'neutral',
  exhausted: 'error',
  error: 'error',
};

export const SearchApiKeyManager: React.FC<SearchApiKeyManagerProps> = ({
  providerName,
  disabled = false,
  className = '',
  onRefresh,
  onKeysChange,
}) => {
  const [keys, setKeys] = useState<SearchApiKeyInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [operationLoading, setOperationLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editKeyIndex, setEditKeyIndex] = useState<number | null>(null);
  const [newKeyValue, setNewKeyValue] = useState('');
  const [editKeyValue, setEditKeyValue] = useState('');
  const [showKeyValue, setShowKeyValue] = useState<Record<number, boolean>>({});
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await searchAdminApi.listApiKeys(undefined, providerName);
      setKeys(result);
      onKeysChange?.(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载密钥列表失败');
    } finally {
      setLoading(false);
    }
  }, [providerName, onKeysChange]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleAddKey = useCallback(async () => {
    if (!newKeyValue.trim()) {
      setError('API 密钥不能为空');
      return;
    }
    setOperationLoading(true);
    setError(null);
    try {
      await searchAdminApi.addApiKey(undefined, providerName, { api_key: newKeyValue });
      setNewKeyValue('');
      setShowAddForm(false);
      await fetchKeys();
      onRefresh?.();
      setSuccess('密钥添加成功');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '添加密钥失败');
    } finally {
      setOperationLoading(false);
    }
  }, [providerName, newKeyValue, fetchKeys, onRefresh]);

  const handleUpdateKey = useCallback(async () => {
    if (editKeyIndex === null || !editKeyValue.trim()) {
      setError('API 密钥不能为空');
      return;
    }
    setOperationLoading(true);
    setError(null);
    try {
      await searchAdminApi.updateApiKey(undefined, providerName, editKeyIndex, { api_key: editKeyValue });
      setEditKeyIndex(null);
      setEditKeyValue('');
      await fetchKeys();
      onRefresh?.();
      setSuccess('密钥更新成功');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新密钥失败');
    } finally {
      setOperationLoading(false);
    }
  }, [providerName, editKeyIndex, editKeyValue, fetchKeys, onRefresh]);

  const handleDeleteKey = useCallback(async (keyIndex: number) => {
    if (confirmDelete !== keyIndex) {
      setConfirmDelete(keyIndex);
      setTimeout(() => setConfirmDelete(null), 3000);
      return;
    }
    setOperationLoading(true);
    setError(null);
    setConfirmDelete(null);
    try {
      await searchAdminApi.deleteApiKey(undefined, providerName, keyIndex);
      await fetchKeys();
      onRefresh?.();
      setSuccess('密钥删除成功');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除密钥失败');
    } finally {
      setOperationLoading(false);
    }
  }, [providerName, confirmDelete, fetchKeys, onRefresh]);

  const handleEnableKey = useCallback(async (keyIndex: number) => {
    setOperationLoading(true);
    setError(null);
    try {
      await searchAdminApi.enableApiKey(undefined, providerName, keyIndex);
      await fetchKeys();
      onRefresh?.();
      setSuccess('密钥已启用');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '启用密钥失败');
    } finally {
      setOperationLoading(false);
    }
  }, [providerName, fetchKeys, onRefresh]);

  const handleDisableKey = useCallback(async (keyIndex: number, reason?: string) => {
    setOperationLoading(true);
    setError(null);
    try {
      await searchAdminApi.disableApiKey(undefined, providerName, keyIndex, reason);
      await fetchKeys();
      onRefresh?.();
      setSuccess('密钥已禁用');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '禁用密钥失败');
    } finally {
      setOperationLoading(false);
    }
  }, [providerName, fetchKeys, onRefresh]);

  const startEdit = useCallback((keyIndex: number, currentKey: string) => {
    setEditKeyIndex(keyIndex);
    setEditKeyValue(currentKey);
    setShowAddForm(false);
  }, []);

  const isEnabled = (status: string) => status === 'enabled' || status === 'active';
  const enabledCount = keys.filter(k => isEnabled(k.status)).length;

  if (loading) {
    return (
      <div className={clsx('p-4 text-center text-gray-500', className)}>
        <div className="animate-spin w-6 h-6 border-3 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
        加载密钥列表...
      </div>
    );
  }

  return (
    <div className={clsx('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h4 className="text-sm font-medium text-gray-700">API 密钥管理</h4>
          <StatusBadge variant={enabledCount > 0 ? 'success' : 'error'} size="sm" dot>
            {enabledCount}/{keys.length} 可用
          </StatusBadge>
        </div>
        {!disabled && !showAddForm && editKeyIndex === null && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            disabled={operationLoading}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            添加密钥
          </button>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">关闭</button>
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{success}</div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h5 className="text-sm font-medium text-gray-700 mb-3">添加新密钥</h5>
          <div className="flex gap-3">
            <input
              type="password"
              value={newKeyValue}
              onChange={(e) => setNewKeyValue(e.target.value)}
              placeholder="输入 API 密钥"
              disabled={operationLoading}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={handleAddKey}
              disabled={operationLoading || !newKeyValue.trim()}
              className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              {operationLoading ? '添加中...' : '添加'}
            </button>
            <button
              type="button"
              onClick={() => { setShowAddForm(false); setNewKeyValue(''); }}
              disabled={operationLoading}
              className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editKeyIndex !== null && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h5 className="text-sm font-medium text-gray-700 mb-3">编辑密钥 #{editKeyIndex}</h5>
          <div className="flex gap-3">
            <input
              type={showKeyValue[editKeyIndex] ? 'text' : 'password'}
              value={editKeyValue}
              onChange={(e) => setEditKeyValue(e.target.value)}
              placeholder="输入新的 API 密钥"
              disabled={operationLoading}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => setShowKeyValue({ ...showKeyValue, [editKeyIndex]: !showKeyValue[editKeyIndex] })}
              className="px-2 py-2 text-gray-400 hover:text-gray-600"
            >
              {showKeyValue[editKeyIndex] ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7" />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={handleUpdateKey}
              disabled={operationLoading || !editKeyValue.trim()}
              className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              {operationLoading ? '更新中...' : '更新'}
            </button>
            <button
              type="button"
              onClick={() => { setEditKeyIndex(null); setEditKeyValue(''); }}
              disabled={operationLoading}
              className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* Keys List */}
      {keys.length === 0 ? (
        <div className="text-center py-4 text-gray-500 text-sm border border-dashed border-gray-300 rounded-lg">
          暂无密钥，点击"添加密钥"按钮添加
        </div>
      ) : (
        <div className="space-y-2">
          {keys.map((key) => (
            <div
              key={key.key_index}
              className={clsx(
                'flex items-center justify-between p-3 rounded-lg border',
                isEnabled(key.status) ? 'bg-gray-50 border-gray-200' : 'bg-gray-100 border-gray-300'
              )}
            >
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500"># {key.key_index}</span>
                <code className="text-sm font-mono bg-white px-2 py-0.5 rounded border border-gray-200">
                  {key.key_suffix}
                </code>
                <StatusBadge variant={statusVariantMap[key.status] || 'neutral'} size="sm">
                  {key.status}
                </StatusBadge>
                {key.error_count > 0 && (
                  <span className="text-xs text-red-500">{key.error_count} 错误</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!disabled && !showAddForm && editKeyIndex === null && (
                  <>
                    <button
                      type="button"
                      onClick={() => startEdit(key.key_index, '')}
                      disabled={operationLoading}
                      className="px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-50"
                    >
                      编辑
                    </button>
                    {isEnabled(key.status) ? (
                      <button
                        type="button"
                        onClick={() => handleDisableKey(key.key_index)}
                        disabled={operationLoading}
                        className="px-2 py-1 text-xs bg-yellow-50 text-yellow-600 border border-yellow-200 rounded hover:bg-yellow-100"
                      >
                        禁用
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleEnableKey(key.key_index)}
                        disabled={operationLoading}
                        className="px-2 py-1 text-xs bg-green-50 text-green-600 border border-green-200 rounded hover:bg-green-100"
                      >
                        启用
                      </button>
                    )}
                    {confirmDelete === key.key_index ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleDeleteKey(key.key_index)}
                          disabled={operationLoading}
                          className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          确认
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(null)}
                          className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
                        >
                          取消
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleDeleteKey(key.key_index)}
                        disabled={operationLoading}
                        className="px-2 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100"
                      >
                        删除
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Help Text */}
      <div className="text-xs text-gray-500">
        提示：每个供应商至少保留一个密钥。禁用的密钥不会参与请求路由。
      </div>
    </div>
  );
};

export default SearchApiKeyManager;